<template>
  <div class="page">
    <ActionBar :breadcrumbItems="breadcrumbItems">
      <Actions
        v-if="selectedCompany"
        :selectedLines="selectedLines.count"
        :filters="filters"
        @clearSelectedLines="handleClearSelectedLines"
        @deleteSelectedEmployees="openMultipleDeleteDialog"
        @reloadData="closeGlobalDrawerAction"
      />
    </ActionBar>

    <TableFilters :status="currentStatus" @change="updateFilters" />

    <Table
      type="employee"
      :status="currentStatus"
      :headers="table.headers"
      :items="table.items"
      :pagination="pagination"
      :activeLine="table.activeLine"
      :dropdownMenu="table.dropdownMenu"
      :lineClick="lineClick"
      :clearSelectedLines="clearSelectedLines"
      :fillHeightDiff="64"
      checkbox
      striped
      hover
      @updateCheckbox="handleUpdateCheckbox"
      @handlePage="handlePage"
      @handleActiveLine="handleActiveLine"
      @tryAgain="loadData"
    />

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawerAction"
    />

    <SingleDeleteDialog
      :visible="singleDeleteDialog"
      :item="selectedItem"
      @close="closeSingleDeleteDialog"
    />

    <MultipleDeleteDialog
      :visible="multipleDeleteDialog"
      :items="selectedLines.ids"
      @close="closeMultipleDeleteDialog"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import requestStatusMixin from 'mixins/requestStatus'
import globalLoaderMixin from 'mixins/globalLoader'
import globalDrawerMixin from 'mixins/globalDrawer'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED, TABLE_PAGINATION_LIMIT } from 'utils/constants'
import { hasRole } from 'utils/functions'
import { catchError, applyPhoneNumberMask, applyCPFNumberMask, objectToArray } from 'corp-front-vue-commons/src/utils/functions'
import ActionBar from 'components/Actionbar'
import Actions from 'components/pages/employees/Actions'
import TableFilters from 'components/pages/employees/TableFilters'
import Table from 'components/Table'
import GlobalDrawer from 'components/GlobalDrawer'
import SingleDeleteDialog from 'components/pages/employees/dialogs/SingleDeleteDialog'
import MultipleDeleteDialog from 'components/pages/employees/dialogs/MultipleDeleteDialog'

export default {
  name: 'EmployeesPage',
  mixins: [
    requestStatusMixin,
    globalLoaderMixin,
    globalDrawerMixin
  ],
  components: {
    ActionBar,
    Actions,
    TableFilters,
    Table,
    GlobalDrawer,
    SingleDeleteDialog,
    MultipleDeleteDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      breadcrumbItems: [
        { name: 'Employees', title: this.$t('Views.EmployeesPage.title') }
      ],
      namespace: 'Views.EmployeesPage',
      selectedLines: {
        ids: [],
        count: 0,
        allSelected: 0
      },
      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: TABLE_PAGINATION_LIMIT
      },
      filters: {
        search: null
      },
      selectedItem: null,
      clearSelectedLines: false,

      // dialog
      singleDeleteDialog: false,
      multipleDeleteDialog: false,

      // drawer
      drawerVisibility: true,

      table: {
        activeLine: null,
        headers: [],
        items: [],
        dropdownMenu: [
          {
            text: this.$t('Views.EmployeesPage.table.dropdown.editEmployee'),
            visible: hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID'),
            action: (employee) => {
              this.$router.push({
                name: 'Employee',
                params: { type: 'editar', id: employee.id }
              })
            }
          },
          {
            text: this.$t('Views.EmployeesPage.table.dropdown.sendEmail'),
            visible: hasRole('employee', 'ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL'),
            action: (employee) => this.handleResendWelcomeEmail(employee)
          },
          {
            text: this.$t('Views.EmployeesPage.table.dropdown.addToAllCostCenters'),
            visible: hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS'),
            action: (employee) => this.handleAddToAllCostCenters(employee)
          },
          {
            divider: true,
            visible: (hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID') || hasRole('employee', 'ROLE_POST_EMPLOYEE_ID_RESENDWELCOMEEMAIL') || hasRole('employee', 'ROLE_PUT_EMPLOYEE_ID_ADDTOALLCOSTCENTERS')) && hasRole('employee', 'ROLE_DELETE_EMPLOYEE_ID')
          },
          {
            text: this.$t('Views.EmployeesPage.table.dropdown.deleteEmployee'),
            visible: hasRole('employee', 'ROLE_DELETE_EMPLOYEE_ID'),
            action: (employee) => this.openSingleDeleteDialog(employee)
          }
        ]
      }
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    async loadData () {
      this.setStatus(STATUS_IS_LOADING)

      try {
        const { currentPage, itemsPerPage } = this.pagination
        const company = this.selectedCompany ? this.selectedCompany.id : null
        const { search } = this.filters
        const { data: res } = await this.$apiService.employee.get(
          { page: currentPage },
          { limit: itemsPerPage },
          { company },
          { search: this.searchIsPhoneNumber(search) }
        )

        this.table.items = this.renderTBodyAttributes(res.data)
        this.updatePagination(res, itemsPerPage)
        this.setStatus(STATUS_SUCCESS)
      } catch (error) {
        this.setStatus(STATUS_FAILED)
        catchError(error)
      }
    },

    searchIsPhoneNumber (str) {
      const regex = /^\(?\d{2}\)?\s?\d\s?\d{4}-?\d{4}$/
      return regex.test(str) ? str.replace(/\D/g, '') : str
    },

    renderTHeadAttributes () {
      const headers = objectToArray(this.$t(`${this.namespace}.table.headers`))
      this.table = {
        ...this.table,
        headers
      }
    },

    renderTBodyAttributes (array) {
      return array.map(employee => {
        const { id, name, company, phone = '', email, nationalId = '' } = employee

        return {
          id,
          name,
          company: company.name,
          phone: applyPhoneNumberMask(phone),
          email,
          nationalId: applyCPFNumberMask(nationalId)
        }
      })
    },

    updatePagination (response = [], itemsPerPage) {
      const totalPagesCalc = Math.ceil(response.length / itemsPerPage)
      const totalPages = totalPagesCalc > 0 ? totalPagesCalc : 1

      this.pagination = {
        ...this.pagination,
        totalElements: response.length,
        totalPages
      }
    },

    updateCurrentPage (page) {
      this.pagination = {
        ...this.pagination,
        currentPage: page
      }
    },

    resetPagination () {
      this.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: TABLE_PAGINATION_LIMIT
      }
    },

    reloadData () {
      this.resetPagination()
      this.loadData()
    },

    handlePage (page) {
      scrollTo(0, 0)
      this.updateCurrentPage(page)
      this.loadData()
    },

    updateFilters (filters) {
      this.filters = { ...filters }
      this.reloadData()
    },

    updateTable (obj) {
      this.table = {
        ...this.table,
        ...obj
      }
    },

    handleUpdateCheckbox (ids) {
      const allSelected = ids.length === this.table.items.length

      this.selectedLines = {
        ...this.selectedLines,
        ids,
        count: ids.length,
        allSelected
      }
    },

    handleClearSelectedLines () {
      this.selectedLines = {
        ids: [],
        count: 0,
        allSelected: 0
      }

      this.clearSelectedLines = !this.clearSelectedLines
    },

    async handleResendWelcomeEmail (employee) {
      this.openGlobalLoader()

      try {
        const { id, name } = employee
        await this.$apiService.employee.resendWelcomeEmail(id)

        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.resendEmail.success`).replace('[VAL]', name)
        })

        this.closeGlobalDrawerAction()
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.resendEmail.error`)
        })
        catchError(error)
      }

      this.closeGlobalLoader()
    },

    async handleAddToAllCostCenters (employee) {
      this.openGlobalLoader()

      try {
        const { id } = employee
        await this.$apiService.employee.addToAllCostCenters(id, { id })

        this.showAlert({
          type: 'success',
          message: this.$t(`${this.namespace}.addToAllCostCenters.success`)
        })

        this.closeGlobalDrawerAction()
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.addToAllCostCenters.error`)
        })
        catchError(error)
      }

      this.closeGlobalLoader()
    },

    lineClick (line) {
      this.updateTable({ id: line.id })
      this.openGlobalDrawer({
        type: 'employee',
        data: {
          id: line.id,
          name: line.name
        },
        filters: { employeeId: line.id }
      })
    },

    closeGlobalDrawerAction (reload) {
      this.closeGlobalDrawer(reload)
      this.updateTable({ activeLine: null })
      console.log('DEBUG', 'closeGlobalDrawerAction', reload)
      reload && this.loadData()
    },

    handleActiveLine (item) {
      this.updateTable({ activeLine: item.id })
    },

    openSingleDeleteDialog (employee) {
      this.singleDeleteDialog = true
      this.selectedItem = { ...employee }
    },

    closeSingleDeleteDialog (reload) {
      this.singleDeleteDialog = false
      this.selectedItem = null

      if (reload) {
        this.loadData()
        this.closeGlobalDrawerAction(reload)
      }
    },

    openMultipleDeleteDialog () {
      this.multipleDeleteDialog = true
    },

    closeMultipleDeleteDialog (reload) {
      this.multipleDeleteDialog = false

      if (reload) {
        this.resetPagination()
        this.loadData()
        this.handleClearSelectedLines()
      }
    }
  },
  watch: {
    selectedCompany () {
      this.reloadData()
      this.updateTable({ activeLine: null })
    },

    filters: {
      deep: true,
      handler () {
        this.updateTable({ activeLine: null })
      }
    }
  },
  mounted () {
    this.renderTHeadAttributes()
    this.loadData()
  }
}
</script>
